// Razrješava ikonu/thumbnail za zadanu datoteku (URL ili samo ime/MIME).
// Dijeli ga PictureIcon (avatar/stack render) i FileUploadList (uploads lista)
// da prikaz tipa datoteke ostane konzistentan kroz aplikaciju.

const PICTURE_EXTENSIONS = ["jpg", "jpeg", "png", "gif", "webp"];

const DOC_ICON_MAP: Record<string, string> = {
  csv: "csv",
  doc: "doc",
  docx: "doc",
  pdf: "pdf",
  txt: "txt",
  xls: "xls",
  xlsx: "xls",
  zip: "zip",
  rar: "zip",
};

export interface ResolvedFileIcon {
  isImage: boolean;
  src: string;
}

// Ekstrahira ekstenziju iz zadnjeg segmenta puta, čisteći query string i
// fragment. Naivni `url.split(".").pop()` na `…/x.pdf?token=abc` vraća
// `"pdf?token=abc"` koji ne odgovara nijednom unosu u mapi.
export const getCleanExtension = (urlOrName: string): string => {
  const noQueryOrHash = urlOrName.split("?")[0].split("#")[0];
  const lastSegment = noQueryOrHash.split("/").pop() ?? "";
  const dotIdx = lastSegment.lastIndexOf(".");
  if (dotIdx === -1) return "";
  return lastSegment.slice(dotIdx + 1).toLowerCase();
};

// Slika → vraća url za thumbnail; ostalo → file-type SVG iz /media/icons/documents/.
// Blob URL-ovi se ne mogu razriješiti samo iz stringa; rješavaju se kroz
// `resolveFileIconFromMime` nakon async fetch-a mime-type-a (PictureIcon).
export const resolveFileIconFromUrl = (url: string): ResolvedFileIcon => {
  if (url.startsWith("blob:")) {
    return { isImage: false, src: "/media/icons/documents/file.svg" };
  }
  const ext = getCleanExtension(url);
  if (PICTURE_EXTENSIONS.includes(ext)) {
    return { isImage: true, src: url };
  }
  return {
    isImage: false,
    src: `/media/icons/documents/${DOC_ICON_MAP[ext] ?? "file"}.svg`,
  };
};

// Vraća ikonu prema MIME tipu (za blob URL-ove gdje je ekstenzija nepoznata).
// `null` znači image (caller treba prikazati thumbnail direktno iz url-a).
export const resolveFileIconFromMime = (mime: string): ResolvedFileIcon | null => {
  if (mime.startsWith("image/")) return null;
  if (mime.includes("pdf")) return { isImage: false, src: "/media/icons/documents/pdf.svg" };
  if (mime.includes("spreadsheet") || mime.includes("excel"))
    return { isImage: false, src: "/media/icons/documents/xls.svg" };
  if (mime.includes("document") || mime.includes("word"))
    return { isImage: false, src: "/media/icons/documents/doc.svg" };
  if (mime.includes("csv") || mime.includes("comma-separated"))
    return { isImage: false, src: "/media/icons/documents/csv.svg" };
  if (mime.includes("zip") || mime.includes("compressed"))
    return { isImage: false, src: "/media/icons/documents/zip.svg" };
  if (mime.includes("text/plain"))
    return { isImage: false, src: "/media/icons/documents/txt.svg" };
  return null;
};

// Convenience: razrješava preferirajući naziv datoteke (uvijek ima ekstenziju),
// pa fallbackom na URL. Koristi se kad postoji `originalFileName` (npr. uploads).
export const resolveFileIcon = (
  urlOrName: string,
  fallbackName?: string
): ResolvedFileIcon => {
  const ext = getCleanExtension(urlOrName);
  if (ext) return resolveFileIconFromUrl(urlOrName);
  if (fallbackName) {
    const fbExt = getCleanExtension(fallbackName);
    if (fbExt) {
      if (PICTURE_EXTENSIONS.includes(fbExt)) {
        // Ime sugerira sliku — koristi originalni url kao izvor thumbnaila.
        return { isImage: true, src: urlOrName };
      }
      return {
        isImage: false,
        src: `/media/icons/documents/${DOC_ICON_MAP[fbExt] ?? "file"}.svg`,
      };
    }
  }
  return resolveFileIconFromUrl(urlOrName);
};
